import React, { ReactNode, useState } from "react";
import { LayoutGrid, Lock, LucideIcon, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../theme/useTheme";
import ThemeToggle from "../theme-toggle";
import UserBadge from "../user-badge";
import icons from "../../assets/icons.png";
import logosLight from "../../assets/right_top_logos_black.png";
import logosDark from "../../assets/right_top_logos.png";
import "./styles.scss";

export type AdminSection = "access" | "solutions";

interface AdminShellProps {
  activeSection: AdminSection;
  onSectionChange: (section: AdminSection) => void;
  children: ReactNode;
}

const navigationItems: {
  section: AdminSection;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
}[] = [
  { section: "access", label: "Acessos", icon: Lock },
  { section: "solutions", label: "Soluções", icon: LayoutGrid },
];

const AdminShell: React.FC<AdminShellProps> = ({
  activeSection,
  onSectionChange,
  children,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const logos = isDarkMode ? logosDark : logosLight;

  const selectSection = (section: AdminSection, disabled?: boolean) => {
    if (disabled) {
      return;
    }

    onSectionChange(section);
    setIsDrawerOpen(false);
  };

  const navigation = (location: "sidebar" | "drawer" | "bottom") => (
    <nav
      className={`admin-shell__navigation admin-shell__navigation--${location}`}
      aria-label="Navegação administrativa"
    >
      {navigationItems.map(({ section, label, icon: Icon, disabled }) => {
        const isActive = !disabled && activeSection === section;
        const iconSize = location === "bottom" ? 20 : 18;

        return (
          <button
            key={section}
            type="button"
            className={`admin-shell__nav-item ${
              isActive ? "admin-shell__nav-item--active" : ""
            }${disabled ? " admin-shell__nav-item--disabled" : ""}`}
            onClick={() => selectSection(section, disabled)}
            aria-current={isActive ? "page" : undefined}
            aria-disabled={disabled || undefined}
            disabled={disabled}
          >
            <Icon size={iconSize} aria-hidden="true" />
            <span>{label}</span>
          </button>
        );
      })}
    </nav>
  );

  return (
    <div
      className={`admin-shell app-root ${
        isDarkMode ? "admin-shell--dark app-root--dark" : ""
      }`}
    >
      <header className="admin-shell__header">
        <div className="admin-shell__brand">
          <button
            type="button"
            className="admin-shell__menu-button"
            onClick={() => setIsDrawerOpen(true)}
            aria-label="Abrir menu administrativo"
            aria-expanded={isDrawerOpen}
            aria-controls="admin-shell-drawer"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <img
            className="admin-shell__desktop-logos"
            src={logos}
            alt="Grupo Marista e hospitais"
          />
          <img
            className="admin-shell__mobile-logos"
            src={icons}
            alt="Grupo Marista e hospitais"
          />
        </div>
        <div className="admin-shell__header-actions">
          <ThemeToggle
            isDarkMode={isDarkMode}
            onToggle={toggleTheme}
          />
          <UserBadge
            user={{ name: user?.name || "" }}
            isDarkMode={isDarkMode}
          />
        </div>
      </header>

      {isDrawerOpen && (
        <div className="admin-shell__drawer-layer">
          <button
            type="button"
            className="admin-shell__overlay"
            onClick={() => setIsDrawerOpen(false)}
            aria-label="Fechar menu administrativo"
          />
          <aside
            id="admin-shell-drawer"
            className="admin-shell__drawer"
            aria-label="Menu administrativo"
          >
            <div className="admin-shell__drawer-header">
              <img src={logos} alt="Grupo Marista e hospitais" />
              <button
                type="button"
                className="admin-shell__close-button"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Fechar menu administrativo"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            {navigation("drawer")}
          </aside>
        </div>
      )}

      <div className="admin-shell__body">
        <aside
          className="admin-shell__sidebar"
          aria-label="Menu administrativo"
        >
          {navigation("sidebar")}
        </aside>
        <main className="admin-shell__main">{children}</main>
      </div>

      {navigation("bottom")}

      <footer className="admin-shell__footer">
        <span>2025 © Hospital São Marcelino Champagnat, Inc.</span>
        <a
          href="https://privacidade.grupomarista.org.br/privacidade/"
          target="_blank"
          rel="noreferrer"
        >
          Política de Privacidade
        </a>
      </footer>
    </div>
  );
};

export default AdminShell;
